/**
 * ExercisesHub — Exercices tab content with curated difficulty levels.
 *
 * Two usage modes:
 *
 * 1) Curriculum mode (preferred): pass `levels` = {
 *      easy:   { skills:[..], lessonIds:[..], label?, hint? },
 *      medium: { ... },
 *      hard:   { ... },
 *    }
 *    The hub shows 3 cards (Facile / Moyen / Difficile) with their own
 *    skill scope so a real progression is exposed.
 *
 * 2) Single-skill mode (fallback): pass `section + skill | ruleId`. The
 *    cards filter by `difficulty` only.
 *
 * Click on a card → list of exercises with status (réussi / raté / non fait)
 * and score. Clicking « Tout faire en série » or a single item launches
 * `ExerciseSeriesPlayer`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Play, Sparkles, ArrowLeft, CheckCircle2, XCircle, Circle, AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { coursesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ExerciseSeriesPlayer } from './ExerciseSeriesPlayer';

const DIFF_LABEL = {
  easy:   { fr: 'Facile',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  medium: { fr: 'Moyen',     cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  hard:   { fr: 'Difficile', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};

const SERIES_LIMIT = 10;

const csv = (arr) => (arr && arr.length ? arr.join(',') : null);

const buildFilter = (base, levelCfg, difficulty) => ({
  section: base.section,
  ruleId: base.ruleId || levelCfg?.ruleId || null,
  skills: csv(levelCfg?.skills),
  skill: !levelCfg?.skills ? base.skill : null,
  lessonIds: csv(levelCfg?.lessonIds),
  difficulty: levelCfg?.skipDifficulty ? null : difficulty,
  limit: 200,
});

// ---------------------------------------------------------------------------
// Drilldown: exercise list with per-item status
// ---------------------------------------------------------------------------
const labelFr = (s) => (typeof s === 'string' ? s : s?.fr || '');

// Compute a per-exercise mastery bucket from `{attempts, correct}`.
// Mirrors the user's request: green ≥80%, orange 50-79%, red <50%, grey not started.
const scoreBucket = (status) => {
  if (!status || !status.attempts) return 'todo';
  const pct = (status.correct / status.attempts) * 100;
  if (pct >= 80) return 'mastered';
  if (pct >= 50) return 'in_progress';
  return 'failing';
};

const StatusIcon = ({ bucket }) => {
  if (bucket === 'mastered')
    return <CheckCircle2 className="w-4 h-4 text-emerald-400" data-testid="exo-row-status-mastered" />;
  if (bucket === 'in_progress')
    return <AlertCircle className="w-4 h-4 text-amber-400" data-testid="exo-row-status-in-progress" />;
  if (bucket === 'failing')
    return <XCircle className="w-4 h-4 text-rose-400" data-testid="exo-row-status-failing" />;
  return <Circle className="w-4 h-4 text-text-muted" data-testid="exo-row-status-todo" />;
};

const ExerciseRow = ({ ex, status, onClick, testid }) => {
  const display = ex.display || {};
  const previewArabic = display.letter || display.arabicText
    || (display.audioUrl ? '\uD83D\uDD0A' : '');
  const bucket = scoreBucket(status);
  const pct = status?.attempts
    ? Math.round((status.correct / status.attempts) * 100)
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-surface-elevated/30 hover:border-primary/40 hover:bg-primary/5 transition-colors text-left"
      data-testid={testid}
      data-status-bucket={bucket}
    >
      <StatusIcon bucket={bucket} />
      <span className="font-arabic text-xl text-primary" dir="rtl">
        {previewArabic || '·'}
      </span>
      <span className="flex-1 text-sm text-text-secondary truncate">
        {labelFr(ex.title) || ex.id}
      </span>
      {status?.attempts > 0 && (
        <Badge variant="outline" className="text-[10px]">
          {pct}% · {status.correct}/{status.attempts}
        </Badge>
      )}
      <Play className="w-3.5 h-3.5 text-text-muted" />
    </button>
  );
};

const ExerciseListView = ({
  filter,
  diffLabel,
  testidPrefix,
  onBack,
  onPlayAll,
  onPlayFrom,
}) => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [statusById, setStatusById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    coursesApi.listExercises(filter).then(async (r) => {
      if (cancel) return;
      const list = r.data?.items || [];
      setItems(list);
      if (isAuthenticated && list.length) {
        try {
          const s = await coursesApi.getExerciseStatus(list.map((e) => e.id));
          if (!cancel) setStatusById(s.data?.byId || {});
        } catch { /* ignore */ }
      }
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [JSON.stringify(filter), isAuthenticated]); // eslint-disable-line

  const stats = useMemo(() => {
    const total = items.length;
    const counts = { mastered: 0, in_progress: 0, failing: 0, todo: 0, done: 0 };
    for (const ex of items) {
      const s = statusById[ex.id];
      const b = scoreBucket(s);
      counts[b] = (counts[b] || 0) + 1;
      if (s && s.attempts > 0) counts.done += 1;
    }
    return { total, ...counts };
  }, [items, statusById]);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-list`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-text-muted"
          onClick={onBack}
          data-testid={`${testidPrefix}-list-back`}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux niveaux
        </Button>
        <div className="text-xs text-text-muted flex items-center gap-2 flex-wrap">
          <span data-testid={`${testidPrefix}-list-stats`}>
            {stats.done}/{stats.total} faits
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1" data-testid={`${testidPrefix}-stats-mastered`}>
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              {stats.mastered}
            </span>
            <span className="inline-flex items-center gap-1" data-testid={`${testidPrefix}-stats-in-progress`}>
              <AlertCircle className="w-3 h-3 text-amber-400" />
              {stats.in_progress}
            </span>
            <span className="inline-flex items-center gap-1" data-testid={`${testidPrefix}-stats-failing`}>
              <XCircle className="w-3 h-3 text-rose-400" />
              {stats.failing}
            </span>
            <span className="inline-flex items-center gap-1" data-testid={`${testidPrefix}-stats-todo`}>
              <Circle className="w-3 h-3 text-text-muted" />
              {stats.todo}
            </span>
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface-elevated/40 p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h4 className="text-sm font-medium text-text-primary">
              Niveau {diffLabel.toLowerCase()} · {items.length} exercices
            </h4>
          </div>
          <Button
            size="sm"
            disabled={!items.length}
            onClick={() => onPlayAll(items)}
            data-testid={`${testidPrefix}-list-play-all`}
          >
            <Play className="w-3 h-3 mr-1" /> Faire la série
          </Button>
        </div>

        {loading && (
          <p className="text-sm text-text-muted">Chargement…</p>
        )}

        {!loading && !items.length && (
          <p className="text-sm text-text-muted">Aucun exercice à ce niveau.</p>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-1.5 max-h-[480px] overflow-auto pr-1">
            {items.map((ex, i) => (
              <ExerciseRow
                key={ex.id}
                ex={ex}
                status={statusById[ex.id]}
                onClick={() => onPlayFrom(items, i)}
                testid={`${testidPrefix}-row-${ex.id}`}
              />
            ))}
          </div>
        )}

        {!isAuthenticated && (
          <p className="text-[11px] text-text-muted">
            Connectez-vous pour suivre votre score sur chaque exercice.
          </p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main hub
// ---------------------------------------------------------------------------
export const ExercisesHub = ({
  section,                // 'reading' | 'tajweed'
  skill = null,
  ruleId = null,
  lessonId = null,
  levels = null,          // optional curriculum {easy,medium,hard}
  title = 'Exercices',
  testidPrefix = 'exo-hub',
}) => {
  const [counts, setCounts] = useState({ easy: 0, medium: 0, hard: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ kind: 'cards' });
  // view = {kind:'cards'} | {kind:'list', diff} | {kind:'play', items, startAt}

  const base = { section, skill, ruleId, lessonId };

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setView({ kind: 'cards' });

    const fetchOne = (filter) =>
      coursesApi.listExercises(filter).then((r) => r.data?.items?.length || 0)
        .catch(() => 0);

    if (levels) {
      Promise.all([
        fetchOne(buildFilter(base, levels.easy, 'easy')),
        fetchOne(buildFilter(base, levels.medium, 'medium')),
        fetchOne(buildFilter(base, levels.hard, 'hard')),
      ]).then(([e, m, h]) => {
        if (cancel) return;
        setCounts({ easy: e, medium: m, hard: h, total: e + m + h });
      }).finally(() => !cancel && setLoading(false));
    } else {
      coursesApi.listExercises({ section, skill, ruleId, lessonId, limit: 500 })
        .then((r) => {
          if (cancel) return;
          const items = r.data?.items || [];
          const by = { easy: 0, medium: 0, hard: 0 };
          items.forEach((i) => { by[i.difficulty] = (by[i.difficulty] || 0) + 1; });
          setCounts({ ...by, total: items.length });
        }).finally(() => !cancel && setLoading(false));
    }
    return () => { cancel = true; };
  }, [section, skill, ruleId, lessonId, JSON.stringify(levels)]); // eslint-disable-line

  // --- play view ---
  if (view.kind === 'play') {
    return (
      <div data-testid={`${testidPrefix}-running`}>
        <Button
          variant="ghost"
          size="sm"
          className="mb-3 text-text-muted"
          onClick={() => setView({ kind: 'list', diff: view.diff })}
          data-testid={`${testidPrefix}-back`}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
        </Button>
        <ExerciseSeriesPlayer
          exercises={view.items}
          testidPrefix={`${testidPrefix}-player`}
          onExit={() => setView({ kind: 'list', diff: view.diff })}
        />
      </div>
    );
  }

  // --- list (drilldown) view ---
  if (view.kind === 'list') {
    const cfg = levels ? levels[view.diff] : null;
    const filter = levels
      ? buildFilter(base, cfg, view.diff)
      : { section, skill, ruleId, lessonId, difficulty: view.diff, limit: 200 };
    return (
      <ExerciseListView
        filter={filter}
        diffLabel={DIFF_LABEL[view.diff].fr}
        testidPrefix={`${testidPrefix}-${view.diff}`}
        onBack={() => setView({ kind: 'cards' })}
        onPlayAll={(items) => setView({
          kind: 'play', diff: view.diff,
          items: items.slice(0, SERIES_LIMIT),
        })}
        onPlayFrom={(items, idx) => setView({
          kind: 'play', diff: view.diff,
          // play from this index forward, then loop back to fill up to LIMIT
          items: [...items.slice(idx), ...items.slice(0, idx)].slice(0, SERIES_LIMIT),
        })}
      />
    );
  }

  // --- cards view ---
  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-surface-elevated/30 p-6 text-sm text-text-muted"
           data-testid={`${testidPrefix}-loading`}>
        Chargement des séries…
      </div>
    );
  }

  if (!counts.total) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-elevated/20 p-6 text-sm text-text-muted"
           data-testid={`${testidPrefix}-empty`}>
        Aucun exercice pour le moment.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-hub`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="text-lg font-serif text-text-primary">{title}</h3>
          <Badge variant="outline" className="text-xs">
            {counts.total} exercices
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {['easy', 'medium', 'hard']
          // Hide difficulty cards that contain zero playable exercises so
          // the user never clicks into an empty list (lot 32).
          .filter((diff) => (counts[diff] || 0) > 0)
          .map((diff) => {
          const n = counts[diff] || 0;
          const meta = DIFF_LABEL[diff];
          const cfg = levels ? levels[diff] : null;
          const hint = cfg?.hint || (
            diff === 'easy'   ? 'Bases — lettres, ḥarakāt simples' :
            diff === 'medium' ? 'Syllabes, formes, mots simples' :
                                'Mots du Coran, segments de versets'
          );
          return (
            <button
              key={diff}
              type="button"
              onClick={() => setView({ kind: 'list', diff })}
              className="text-left rounded-lg border p-4 flex flex-col gap-2 transition-colors w-full border-border bg-surface-elevated/40 hover:border-primary/50 hover:bg-primary/5"
              data-testid={`${testidPrefix}-card-${diff}`}
            >
              <div className="flex items-center justify-between">
                <Badge className={`${meta.cls} text-[11px] border`}>{meta.fr}</Badge>
                <span className="text-sm text-text-muted">{n} ex.</span>
              </div>
              <p className="text-xs text-text-muted">{hint}</p>
              <div className="text-[11px] text-primary mt-auto pt-1">
                Voir la liste · commencer →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ExercisesHub;
